import { CloudWatchClient, PutMetricAlarmCommand, PutMetricDataCommand } from '@aws-sdk/client-cloudwatch';

import { serverLogger } from '../utils/server-logger';

let cloudWatchClient: CloudWatchClient | null = null;
let metricsDisabled = false;
const HIGH_LATENCY_THRESHOLD_MS = Number(process.env.HTTP_METRICS_HIGH_LATENCY_THRESHOLD_MS) || 500;
const configuredAlarms = new Set<string>();

function getClient(): CloudWatchClient | null {
  if (metricsDisabled) return null;
  if (cloudWatchClient) return cloudWatchClient;
  try {
    cloudWatchClient = new CloudWatchClient({});
  } catch (err) {
    metricsDisabled = true;
    serverLogger.debug('[http-client] CloudWatch metrics disabled', err);
    return null;
  }
  return cloudWatchClient;
}

async function ensureHighLatencyAlarm(client: CloudWatchClient, path: string, status: number): Promise<void> {
  const key = `${path}|${status}`;
  if (configuredAlarms.has(key)) return;
  try {
    await client.send(
      new PutMetricAlarmCommand({
        AlarmName: `HttpClientHighLatency:${path}:${status}`,
        Namespace: 'HttpClient',
        MetricName: 'HighLatency',
        Dimensions: [
          { Name: 'Path', Value: path },
          { Name: 'Status', Value: String(status) },
        ],
        Statistic: 'Sum',
        Period: 60,
        EvaluationPeriods: 1,
        Threshold: 1,
        ComparisonOperator: 'GreaterThanOrEqualToThreshold',
        TreatMissingData: 'notBreaching',
      }),
    );
    configuredAlarms.add(key);
  } catch (err) {
    serverLogger.debug('[http-client] CloudWatch alarm configuration failed', err);
  }
}

export async function recordHttpMetrics(path: string, status: number, elapsedMs: number): Promise<void> {
  serverLogger.log('[http-client] request', { path, status, latency: elapsedMs });
  const isHighLatency = elapsedMs > HIGH_LATENCY_THRESHOLD_MS;
  if (isHighLatency) {
    serverLogger.warn('[http-client] high latency', {
      path,
      status,
      latency: elapsedMs,
      threshold: HIGH_LATENCY_THRESHOLD_MS,
    });
  }
  const client = getClient();
  if (!client) return;
  const metricData = [
    {
      MetricName: 'Latency',
      Dimensions: [
        { Name: 'Path', Value: path },
        { Name: 'Status', Value: String(status) },
      ],
      Unit: 'Milliseconds',
      Value: elapsedMs,
    },
  ];
  if (isHighLatency) {
    metricData.push({
      MetricName: 'HighLatency',
      Dimensions: [
        { Name: 'Path', Value: path },
        { Name: 'Status', Value: String(status) },
      ],
      Unit: 'Count',
      Value: 1,
    });
  }
  try {
    await client.send(
      new PutMetricDataCommand({
        Namespace: 'HttpClient',
        MetricData: metricData,
      }),
    );
    if (isHighLatency) {
      await ensureHighLatencyAlarm(client, path, status);
    }
  } catch (err) {
    metricsDisabled = true;
    serverLogger.debug('[http-client] CloudWatch metrics disabled', err);
  }
}
